<template>
  <div
    ref="paymentTableRef"
    class="i-payment-table"
  >
    <div class="header">
      <!-- <div>
        <strong
          >{{ comprehensiveReportPurchases }} people purchased your <br v-if="isSmOrLarger" />
          recommended package</strong
        >
        in the last hour.
      </div> -->
      <!-- <div class="row-list">
        <div>
          <span :style="{ color: bilbao500 }">{{ basicReportPurchases }}</span> Basic plans
        </div>
        <div>
          <span :style="{ color: bilbao500 }">{{ comprehensiveReportPurchases }}</span> Comprehensive plans
        </div>
        <div>
          <span :style="{ color: bilbao500 }">{{ premiumReportPurchases }}</span> Premium plans
        </div>
      </div> -->
      <div><strong>Take control</strong> of your career today.</div>
      <div class="sub-header">
        Since you are looking to advance your career, you will find the most success with our
        <span>Comprehensive Report.</span>
      </div>
      <!-- <div class="sub-header">Select the report that best fits you:</div> -->
    </div>
    <div class="prices">
      <iPaymentCard
        v-for="(tier, index) in PAYMENT_TABLE"
        :key="index"
        v-bind="tier"
        :is-button-disabled="isPaymentButtonDisabled"
        @clicked="disablePaymentButtons"
        @payment-action-error="enablePaymentButtons"
      />
    </div>
    <!-- <div class="tax">Local taxes (VAT, GST, etc.) will be charged in addition to the prices mentioned.</div> -->
    <div class="payment">
      <div>
        <div class="mbg"><img :src="IconMBG" />Money-Back Guarantee</div>
        Jobtest.org has a 60-day money back refund policy.
      </div>
      <div>
        <div class="secure"><img :src="IconSecure" />Secure checkout</div>
        We use the same SSL encryption technology that banks use to protect sensitive info.
      </div>
      <div class="logos">
        <img :src="CheckoutBrands" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
// import { useUserStore } from "@/stores/user"
import {
  // breakpointSm,
  breakpointLg
} from "@/styles/variables"
// import { bilbao500 } from "@/styles/colors"

import getPaymentAction from "@/composables/getPaymentAction"
import linkToCoachSelection from "@/composables/linkToCoachSelection"
// import useRandomNumber from "@/composables/useRandomNumber"
import useIsScreenSize from "@/composables/useIsScreenSize"
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

// COMPONENTS
import iPaymentCard from "@/components/pricing/PaymentCardTest.vue"

// ASSETS
import IconSecure from "@/assets/icons/icon-checkout-secure.svg"
import IconMBG from "@/assets/icons/icon-banknote-arrow-up.svg"
import CheckoutBrands from "@/assets/welcome/checkout.avif"

// TYPES
import ReportStatus from "@/shared/enums/ReportStatus"
import StripePrice from "@/shared/enums/StripePrice"
import { Price } from "@/shared/types/Price"
import { CheckoutSessionItem } from "@/shared/types/CheckoutSession"

type TierProps = {
  benefitsPreview: { included: boolean; title: string }[]
  benefits?: { included: boolean; title: string }[]
  buttonText: string
  currentPriceWithCents?: string
  isButtonDisabled?: boolean
  isHighlighted?: boolean
  paymentAction: () => void
  reportStatus: ReportStatus
  subtitle?: string
  title?: string
  description?: string
}

// STORES
// const userStore = useUserStore()

// COMPOSABLES
// const basicReportPurchases = useRandomNumber(userStore.getFullName || "Full Name", 80, 130)
// const comprehensiveReportPurchases = useRandomNumber(userStore.getFullName || "Full Name", 200, 310)
// const premiumReportPurchases = useRandomNumber(userStore.getFullName || "Full Name", 90, 170)
// const isSmOrLarger = useIsScreenSize(breakpointSm)
const isLgOrLarger = useIsScreenSize(breakpointLg)

// CONSTANTS
const STRIPE_PRICE_BASIC = StripePrice.BASIC_2990
const STRIPE_PRICE_COMPREHENSIVE = StripePrice.COMPREHENSIVE_3990
const STRIPE_PRICE_PREMIUM = StripePrice.COACHING_DISCOVERY_ONE

// FUNCTIONS
const disablePaymentButtons = () => {
  isPaymentButtonDisabled.value = true
}

const enablePaymentButtons = () => {
  isPaymentButtonDisabled.value = false
}

/**
 * Fetch the prices and their information from the API
 */
const fetchPrices = async () => {
  try {
    const response = await api.stripe.getPrices([STRIPE_PRICE_BASIC, STRIPE_PRICE_COMPREHENSIVE, STRIPE_PRICE_PREMIUM])
    const pricesArr = response.prices
    prices.value = {
      basic: pricesArr.find((p) => p.uuid === STRIPE_PRICE_BASIC) || null,
      comprehensive: pricesArr.find((p) => p.uuid === STRIPE_PRICE_COMPREHENSIVE) || null,
      premium: pricesArr.find((p) => p.uuid === STRIPE_PRICE_PREMIUM) || null
    }
  } catch (error) {
    console.error("Failed to load prices", error)
  }
}

const sendGTMEvent = async () => {
  const items: CheckoutSessionItem[] = []

  if (prices.value.basic) {
    items.push({
      id: prices.value.basic.productId,
      name: prices.value.basic.name,
      price: prices.value.basic.price / 100,
      quantity: 1
    })
  }

  if (prices.value.comprehensive) {
    items.push({
      id: prices.value.comprehensive.productId,
      name: prices.value.comprehensive.name,
      price: prices.value.comprehensive.price / 100,
      quantity: 1
    })
  }

  if (prices.value.premium) {
    items.push({
      id: prices.value.premium.productId,
      name: prices.value.premium.name,
      price: prices.value.premium.price / 100,
      quantity: 1
    })
  }

  await GtmService.viewItemListEvent(UserFlow.REPORT, items)
}

/**
 * Format the price in cents as a dollar amount, e.g. "29.90"
 */
const formatPrice = (price: number | null | undefined) => {
  if (price === null || price === undefined) return "Not Available"
  return (price / 100).toFixed(2)
}

// REFS
/**
 * Prices for the different report types. Fetched from the API.
 */
const prices = ref<Record<"basic" | "comprehensive" | "premium", Price | null>>({
  basic: null,
  comprehensive: null,
  premium: null
})

/**
 * Payment table data
 */
const PAYMENT_TABLE = computed<TierProps[]>(() => {
  const comprehensiveTier = {
    benefitsPreview: [
      { title: "Your Career Personality", included: true },
      { title: "<span>Your Top <strong><u> 10 Career Paths </u></strong> ✨</span>", included: true },
      { title: "Detailed Career Analysis", included: true },
      { title: "30-Minute Coaching Session", included: false },
      { title: "Dedicated Career Coach", included: false }
    ],
    // benefitsPreview: [
    //   { title: "Top 10 Best-Fit Careers ✨", included: true },
    //   { title: "Top 10 Comprehensive Career Paths, matched to your strengths", included: true },
    //   {
    //     title: "Detailed Analysis of Career-Specific Personality Strengths, Weaknesses, and Areas of Improvement",
    //     included: true
    //   },
    //   { title: "30-minutes coaching session", included: false },
    //   { title: "Dedicated Expert Career Coach", included: false }
    // ],
    // benefits: [
    //   { title: "Top 10 Best-Fit Careers ✨ ", included: true },
    //   { title: "Top 10 Comprehensive Career Paths Based On Your Strengths", included: true },
    //   { title: "Custom Professional Bio for Resume & LinkedIn", included: true },
    //   { title: "Your Workplace Strengths & Weaknesses", included: true },
    //   { title: "Your Unique Career Personality Decoded", included: true },

    //   { title: "Instant Results and Career Dashboard", included: true },
    //   { title: "Job Listings Near You", included: true },
    //   { title: "Up to 2 Recalculations If Your Focus Changes", included: true },
    //   {
    //     title: "Detailed Career Analysis to Understand Target Role Criteria and Your Skill Gaps",
    //     included: true
    //   },
    //   { title: "Access to Exclusive Online Career Coaching Community", included: false },
    //   { title: "Career Transition Progress Tracker", included: false }
    // ],
    buttonText: "I'm Ready to Accelerate",
    currentPriceWithCents: formatPrice(prices.value.comprehensive?.price),
    isHighlighted: true,
    paymentAction: getPaymentAction([STRIPE_PRICE_COMPREHENSIVE], UserFlow.REPORT, enablePaymentButtons),
    reportStatus: ReportStatus.COMPREHENSIVE,
    subtitle: "For career accelerators",
    title: "Comprehensive",
    // description: "Professionals ready to shift, refocus, level up their career and grow with a strategy. ",
    description: "In-Depth Career Analysis"
  }

  const tiersToReturn = [
    {
      benefitsPreview: [
        { title: "Your Career Personality", included: true },
        { title: "<span>Your Top <strong><u> 5 Career Paths</u></strong></span>", included: true },
        { title: "Detailed Career Analysis", included: false },
        { title: "30-Minute Coaching Session", included: false },
        { title: "Dedicated Career Coach", included: false }
      ],
      // benefitsPreview: [
      //   { title: "Top 5 Best-Fit Careers ", included: true },
      //   { title: "Top 5 Comprehnsive  Career Paths, matched to your strengths", included: true },
      //   {
      //     title: "Detailed Analysis of Career-Specific Personality Strengths, Weaknesses, and Areas of Improvement",
      //     included: false
      //   },
      //   { title: "30-minutes coaching session", included: false },
      //   { title: "Dedicated Expert Career Coach", included: false }
      // ],
      // benefits: [
      //   { title: "Top 5 Best-Fit Careers", included: true },
      //   { title: "Top 5 Comprehensive Career Paths Based On Your Strengths", included: true },
      //   { title: "Custom Professional Bio for Resume & LinkedIn", included: true },
      //   { title: "Your Workplace Strengths and Weaknesses", included: true },
      //   { title: "Your Unique Career Personality Decoded", included: true },
      //   { title: "Instant Results and Career Dashboard", included: true },
      //   { title: "Job Listings Near You", included: true },
      //   { title: "Up to 2 Recalculations If Your Focus Changes", included: true },
      //   {
      //     title: "Detailed Career Analysis to Understand Target Role Criteria and Your Skill Gaps",
      //     included: false
      //   },
      //   { title: "Access to Exclusive Online Career Coaching Community", included: false },
      //   { title: "Career Transition Progress Tracker", included: false }
      // ],
      buttonText: "I'm Ready to Explore",
      currentPriceWithCents: formatPrice(prices.value.basic?.price),
      isHighlighted: false,
      paymentAction: getPaymentAction([STRIPE_PRICE_BASIC], UserFlow.REPORT, enablePaymentButtons),
      reportStatus: ReportStatus.BASIC,
      subtitle: "For career explorers",
      title: "Basic",
      // description:
      //   "Career questioners, early-stage pivoters, or anyone who wants a quick, low-risk window into what else is out there."
      description: "Essential Career Insights"
    },
    {
      benefitsPreview: [
        { title: "Your Career Personality", included: true },
        { title: "<span>Your Top <strong><u> 10 Career Paths </u></strong> ✨</span>", included: true },
        { title: "Detailed Career Analysis", included: true },
        { title: "30-Minute Coaching Session", included: true },
        { title: "Dedicated Career Coach", included: true }
      ],
      // benefitsPreview: [
      //   { title: "Top 10 Best-Fit Careers ✨", included: true },
      //   { title: "Top 10 Comprehensive Career Paths Based On Your Strengths", included: true },
      //   {
      //     title: "Detailed Analysis of Career-Specific Personality Strengths, Weaknesses, and Areas of Improvement",
      //     included: true
      //   },
      //   { title: "30-minutes coaching session", included: true },
      //   { title: "Dedicated Expert Career Coach", included: true }
      // ],
      // benefits: [
      //   { title: "Top 10 Best-Fit Careers ✨", included: true },
      //   { title: "Top 10 Comprehensive Career Paths Based On Your Strengths", included: true },
      //   { title: "Custom Professional Bio for Resume & LinkedIn", included: true },
      //   { title: "Your Workplace Strengths & Weaknesses", included: true },
      //   { title: "Your Unique Career Personality Decoded", included: true },
      //   { title: "Instant Results and Career Dashboard", included: true },
      //   { title: "Job Listings Near You", included: true },
      //   { title: "Up to 2 Recalculations If Your Focus Changes", included: true },
      //   {
      //     title: "Detailed Career Analysis to Understand Target Role Criteria and Your Skill Gaps",
      //     included: true
      //   },
      //   { title: "Access to Exclusive Online Career Coaching Community", included: true },
      //   { title: "Career Transition Progress Tracker", included: true }
      // ],
      buttonText: "I'm Ready to 10x My Career",
      currentPriceWithCents: formatPrice(prices.value.premium?.price || 0),
      isButtonDisabled: false,
      isHighlighted: false,
      paymentAction: linkToCoachSelection,
      reportStatus: ReportStatus.PREMIUM,
      subtitle: "For high-growth movers",
      title: "Premium",
      // description: "High-performers, career pivoters, and growth-minded professionals ready to level up now—not later."
      description: "Direct Support From A Career Coach"
    }
  ]

  if (isLgOrLarger.value) tiersToReturn.splice(1, 0, comprehensiveTier)
  else tiersToReturn.unshift(comprehensiveTier)

  return tiersToReturn
})

/**
 * Whether the payment buttons should be disabled
 * - We want to disable this when the user is already in the process of starting a checkout session
 */
const isPaymentButtonDisabled = ref(false)

const paymentTableRef = ref<HTMLElement | null>(null)

// LIFECYCLE
onMounted(() => {
  fetchPrices().then(() => {
    enablePaymentButtons()
  })

  // Send GTM event when the payment table is in view
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendGTMEvent()
          obs.disconnect() // trigger only once
        }
      })
    },
    { threshold: 0.1 }
  )
  if (paymentTableRef.value) observer.observe(paymentTableRef.value)
})
</script>

<style lang="scss" scoped>
.i-payment-table {
  @include flex(column, center, center, $gap: 35px);
  @include text-sm;

  background: linear-gradient(180deg, #fff 0%, #eae7ff 100%);
  padding: 50px 15px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 15px;
    gap: 45px;
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;

      span {
        background-image: $blue-purple-gradient;
        color: transparent;
        background-clip: text;
      }
    }

    .row-list {
      @include flex(row, center, center, wrap, $gap: 6px 20px);
      @include text-sm;

      font-weight: 400;
    }
  }

  .prices {
    @include flex(row, center, stretch, wrap, $gap: 24px);

    width: 100%;
  }

  .tax {
    text-align: center;
  }

  .payment {
    @include border-radius-lg;
    @include flex(column, center, center, $gap: 40px);

    background-color: #fff6;
    box-shadow: 0 0 15px 15px #c5c5c516;
    padding: 32px;
    width: 100%;
    max-width: 1410px;

    .logos {
      max-width: 182px;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, center, center, wrap, $gap: 20px 40px);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: 40px;
    }

    @media screen and (min-width: $breakpoint-xl) {
      gap: 90px;
    }

    > div {
      @include flex(column, center, center, $gap: 10px);

      width: 299px;
      text-align: center;

      @media screen and (min-width: $breakpoint-md) {
        $gap: 16px;
      }

      > div {
        @include flex(row, center, center, $gap: 5px);
        @include text-xl;

        font-weight: 600;
      }

      .secure {
        color: $bilbao500;
      }
    }
  }
}
</style>
